import React, { useState, useEffect, useCallback } from 'react';
import { Spin, Pagination } from 'antd';
import {
  BankOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  RightOutlined,
  DownOutlined
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { ApiService } from '../services/api';
import './ProductsPage.css';

const ProductsPage = () => {
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null); // 展开的主分类
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isHotProducts, setIsHotProducts] = useState(false);
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    companyName: '',
    address: '',
    mobile: '',
    email: ''
  });
  const pageSize = 10;

  // 获取产品分类
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await ApiService.getMainCategoryList();
        
        if (response.code === '0000' && response.data) {
          // 为每个主分类添加空的子分类数组，如果后端返回了childCategoryList则使用，否则为空数组
          const categoriesWithSub = response.data.map(category => ({
            ...category,
            childCategoryList: category.childCategoryList || []
          }));
          setCategories(categoriesWithSub);
        } else {
          setError('获取产品分类失败');
        }
      } catch (err) {
        console.error('获取产品分类失败:', err);
        setError('网络请求失败');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // 获取公司信息
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await ApiService.getCompanyDetail();
        
        if (response.code === '0000' && response.data) {
          setCompanyInfo({
            companyName: response.data[0].companyName || '',
            address: response.data[0].address || '',
            mobile: response.data[0].mobile || '',
            email: response.data[0].email || ''
          });
        }
      } catch (err) {
        console.error('获取公司信息失败:', err);
        // 使用默认值，不显示错误
      }
    };

    fetchCompanyInfo();
  }, []);

  // 获取产品列表
  const fetchProducts = useCallback(async (categoryId, pageIndex = 1, childCategoryId = null) => {
    try {
      setProductsLoading(true);

      const response = await ApiService.getCategoryProductListPage(categoryId, pageIndex, pageSize, childCategoryId);

      if (response.code === '0000' && response.data) {
        // 转换API数据格式为ProductCard组件需要的格式
        const formattedProducts = response.data.map(item => ({
          id: item.productId,
          title: item.productName,
          description: '', // API没有返回描述，可以后续扩展
          image: item.picUrl || '📦', // 如果没有图片URL，使用默认图标
          category: categoryId ? categories.find(cat => cat.categoryId === categoryId)?.categoryName : '全部'
        }));
        
        setProducts(formattedProducts);
        setTotal(response.total || 0);
        setCurrentPage(pageIndex);
      } else {
        setError('获取产品列表失败');
      }
    } catch (err) {
      console.error('获取产品列表失败:', err);
      setError('网络请求失败');
    } finally {
      setProductsLoading(false);
    }
  }, [categories]);

  // 处理URL参数，设置初始筛选状态
  useEffect(() => {
    if (categories.length > 0) {
      const filter = searchParams.get('filter');
      const categoryId = searchParams.get('categoryId');
      
      if (filter === 'hot') {
        // 显示热门产品
        setIsHotProducts(true);
        setSelectedCategory(null);
        setCurrentPage(1);
        // 直接调用热门产品API
        const fetchHotProducts = async () => {
          try {
            setProductsLoading(true);
            setError(null);
            
            const response = await ApiService.getHotProductList();
            
            if (response.code === '0000' && response.data) {
              const formattedProducts = response.data.map(item => ({
                id: item.productId,
                title: item.productName,
                description: '',
                image: item.picUrl || '📦',
                category: '热门产品'
              }));
              
              setProducts(formattedProducts);
              setTotal(formattedProducts.length);
            } else {
              setError('获取热门产品失败');
            }
          } catch (err) {
            console.error('获取热门产品失败:', err);
            setError('网络请求失败');
          } finally {
            setProductsLoading(false);
          }
        };
        
        fetchHotProducts();
      } else if (filter === 'category' && categoryId) {
        // 显示指定分类的产品
        const categoryIdNum = categoryId;
        setIsHotProducts(false);
        setSelectedCategory(categoryIdNum);
        setCurrentPage(1);
        // 直接调用分类产品API（主类查询）
        fetchProducts(categoryIdNum, 1, null);
      } else {
        // 默认显示全部产品
        setIsHotProducts(false);
        setSelectedCategory(null);
        setCurrentPage(1);
        // 直接调用全部产品API
        fetchProducts(null, 1, null);
      }
    }
  }, [categories, searchParams, fetchProducts]);

  // 处理分页变化（仅在用户手动操作时触发）
  useEffect(() => {
    // 只有在没有URL参数的情况下才自动触发初始加载
    const filter = searchParams.get('filter');
    const categoryId = searchParams.get('categoryId');
    
    if (categories.length > 0 && !isHotProducts && !filter && !categoryId) {
      fetchProducts(selectedCategory, 1, selectedSubCategory);
    }
  }, [categories, searchParams, fetchProducts, isHotProducts, selectedCategory, selectedSubCategory]);

  // 处理主分类选择（展开/折叠 或 直接查询）
  const handleCategorySelect = (categoryId) => {
    const category = categories.find(cat => cat.categoryId === categoryId);
    const hasSubCategories = category && category.childCategoryList && category.childCategoryList.length > 0;

    if (hasSubCategories) {
      // 如果有子分类，则展开/折叠
      if (expandedCategory === categoryId) {
        // 如果当前已展开，则折叠
        setExpandedCategory(null);
      } else {
        // 展开当前分类
        setExpandedCategory(categoryId);
      }
    } else {
      // 如果没有子分类，则直接查询该主分类的产品
      setSelectedCategory(categoryId);
      setSelectedSubCategory(null);
      setExpandedCategory(null);
      setIsHotProducts(false);
      setCurrentPage(1); // 重置到第一页
      setIsCategoryMenuOpen(false); // 关闭移动端菜单
      // 主类查询：categoryId传主类ID，childCategoryId传空
      fetchProducts(categoryId, 1, null);
    }
  };

  // 处理主分类全部选择（查询主分类所有产品）
  const handleMainCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedSubCategory(null);
    setIsHotProducts(false);
    setCurrentPage(1); // 重置到第一页
    setIsCategoryMenuOpen(false); // 关闭移动端菜单
    // 主类查询：categoryId传主类ID，childCategoryId传空
    fetchProducts(categoryId, 1, null);
  };

  // 处理子分类选择
  const handleSubCategorySelect = (subCategoryId, parentCategoryId) => {
    setSelectedCategory(parentCategoryId);
    setSelectedSubCategory(subCategoryId);
    setIsHotProducts(false);
    setCurrentPage(1); // 重置到第一页
    setIsCategoryMenuOpen(false); // 关闭移动端菜单
    // 子类查询：categoryId传主类ID，childCategoryId传子类ID
    fetchProducts(parentCategoryId, 1, subCategoryId);
  };

  // 显示所有产品
  const handleShowAll = () => {
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setExpandedCategory(null);
    setIsHotProducts(false);
    setCurrentPage(1); // 重置到第一页
    setIsCategoryMenuOpen(false); // 关闭移动端菜单
    // 直接调用全部产品API
    fetchProducts(null, 1, null);
  };

  // 显示热门产品
  const handleShowHotProducts = async () => {
    try {
      setProductsLoading(true);
      setError(null);
      setIsHotProducts(true);
      setSelectedCategory(null);
      setSelectedSubCategory(null);
      setExpandedCategory(null);
      setIsCategoryMenuOpen(false); // 关闭移动端菜单
      
      const response = await ApiService.getHotProductList();
      
      if (response.code === '0000' && response.data) {
        // 转换API数据格式为ProductCard组件需要的格式
        const formattedProducts = response.data.map(item => ({
          id: item.productId,
          title: item.productName,
          description: '',
          image: item.picUrl || '📦',
          category: '热门产品'
        }));
        
        setProducts(formattedProducts);
        setTotal(formattedProducts.length);
        setCurrentPage(1);
      } else {
        setError('获取热门产品失败');
      }
    } catch (err) {
      console.error('获取热门产品失败:', err);
      setError('网络请求失败');
    } finally {
      setProductsLoading(false);
    }
  };

  // 处理分页变化
  const handlePageChange = (page) => {
    if (isHotProducts) {
      // 热门产品不分页，直接跳转
      return;
    }
    fetchProducts(selectedCategory, page, selectedSubCategory);
  };

  // 切换分类菜单显示状态
  const toggleCategoryMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCategoryMenuOpen(!isCategoryMenuOpen);
  };

  // 点击外部区域关闭菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCategoryMenuOpen && !event.target.closest('.category-sidebar')) {
        setIsCategoryMenuOpen(false);
      }
    };

    if (isCategoryMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isCategoryMenuOpen]);

  if (loading) {
    return (
      <div className="products-page">
        <div className="container">
          <div className="page-header">
            <h1>产品中心</h1>
            <p>Product Center</p>
          </div>
          <div className="loading-container">
            <Spin size="large" />
            <p>加载产品分类中...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="products-page">
        <div className="container">
          <div className="page-header">
            <h1>产品中心</h1>
            <p>Product Center</p>
          </div>
          <div className="error-container">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="page-header">
        <h1>产品中心</h1>
        <p>Product Center</p>
      </div>

      <div className="products-layout">
        {/* 左侧分类侧边栏 */}
        <div className="category-sidebar">
          <div className="sidebar-header">
            <div className="sidebar-title">产品分类</div>
            <button className="category-menu-toggle" onClick={toggleCategoryMenu}>
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
          <div className={`category-list ${isCategoryMenuOpen ? 'category-menu-open' : ''}`}>
            <div 
              className={`category-item ${!selectedCategory && !isHotProducts ? 'active' : ''}`}
              onClick={handleShowAll}
            >
              全部产品
            </div>
            <div 
              className={`category-item ${isHotProducts ? 'active' : ''}`}
              onClick={handleShowHotProducts}
            >
              热门产品
            </div>
            {categories.map(category => (
              <div key={category.categoryId}>
                {/* 主分类 */}
                <div
                  className={`category-item ${selectedCategory === category.categoryId && !selectedSubCategory ? 'active' : ''}`}
                  onClick={() => handleCategorySelect(category.categoryId)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                >
                  <span>{category.categoryName}</span>
                  {category.childCategoryList && category.childCategoryList.length > 0 && (
                    expandedCategory === category.categoryId ?
                      <DownOutlined style={{ fontSize: '12px' }} /> :
                      <RightOutlined style={{ fontSize: '12px' }} />
                  )}
                </div>

                {/* 子分类 */}
                {expandedCategory === category.categoryId && category.childCategoryList && category.childCategoryList.length > 0 && (
                  <div className="subcategory-list">
                    {/* 主分类全部选项 */}
                    <div
                      className={`subcategory-item ${selectedCategory === category.categoryId && !selectedSubCategory ? 'active' : ''}`}
                      onClick={() => handleMainCategorySelect(category.categoryId)}
                      style={{ fontWeight: '600', borderBottom: '1px solid rgba(255, 255, 255, 0.2)', marginBottom: '5px', paddingBottom: '8px' }}
                    >
                      全部产品
                    </div>
                    {/* 子分类选项 */}
                    {category.childCategoryList.map(subCategory => (
                      <div
                        key={subCategory.categoryId}
                        className={`subcategory-item ${selectedSubCategory === subCategory.categoryId ? 'active' : ''}`}
                        onClick={() => handleSubCategorySelect(subCategory.categoryId, category.categoryId)}
                      >
                        {subCategory.categoryName}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* 公司信息 */}
          <div className="company-info">
            <div className="company-details">
              <div className="company-item">
                <BankOutlined className="company-icon" />
                <span className="company-value">{companyInfo.companyName}</span>
              </div>
              <div className="company-item">
                <EnvironmentOutlined className="company-icon" />
                <span className="company-value">{companyInfo.address}</span>
              </div>
              <div className="company-item">
                <PhoneOutlined className="company-icon" />
                <span className="company-value">{companyInfo.mobile}</span>
              </div>
              <div className="company-item">
                <MailOutlined className="company-icon" />
                <span className="company-value">{companyInfo.email}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 右侧产品展示区域 */}
        <div className="products-content">
          <div className="content-header">
            <h2>
              {isHotProducts ? '热门产品' :
               selectedSubCategory ?
                 // 查找子分类名称
                 (() => {
                   const parentCategory = categories.find(cat => cat.categoryId === selectedCategory);
                   const subCategory = parentCategory?.childCategoryList?.find(sub => sub.categoryId === selectedSubCategory);
                   return subCategory ? subCategory.categoryName : '产品列表';
                 })() :
               selectedCategory ? categories.find(cat => cat.categoryId === selectedCategory)?.categoryName : '全部产品'}
            </h2>
            <p>共{total}个产品</p>
          </div>

          <div className="products-grid">
            {productsLoading ? (
              <div className="loading-container">
                <Spin size="large" />
                <p>加载产品中...</p>
              </div>
            ) : products.length > 0 ? (
              products.map(product => (
                <ProductCard key={product.id} product={product} />
              ))
            ) : (
              <div className="no-products">
                <p>暂无产品数据</p>
              </div>
            )}
          </div>

          {/* 分页组件 */}
          {total > 0 && !isHotProducts && (
            <div className="pagination-container">
              <Pagination
                current={currentPage}
                total={total}
                pageSize={pageSize}
                onChange={handlePageChange}
                showSizeChanger={false}
                showTotal={(total, range) => 
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
